import type { Sheet } from "@fortune-sheet/core";
import { useCallback, useEffect, useRef, useState } from "react";
import { createWeekStaffScheduleExport } from "../../../../../downloads/weekStaffScheduleExport/createWeekStaffScheduleExport";
import type { CreateWeekStaffScheduleExportArgs } from "../../../../../downloads/weekStaffScheduleExport/types";
import { excelBufferToFortuneSheet } from "../../../../../utils/excelToFortuneSheet";
import { PREVIEW_DEBOUNCE_MS } from "./consts";

type Params = {
	/** Null while the popup is closed, so nothing is built until it is needed. */
	buildArgs: CreateWeekStaffScheduleExportArgs | null;
	onError: (error: unknown) => void;
};

/**
 * Build the workbook and parse it back into spreadsheet-preview sheets.
 *
 * The preview is the real file, round-tripped — so what the user approves is byte-for-byte what
 * downloads, and colors/borders/widths need no second implementation.
 */
export const useWeekStaffScheduleExportPreview = ({ buildArgs, onError }: Params) => {
	const [sheets, setSheets] = useState<Sheet[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	// Monotonic id: a slower earlier build must never overwrite a newer one's result.
	const loadId = useRef(0);
	const onErrorRef = useRef(onError);
	onErrorRef.current = onError;

	const load = useCallback(async (args: CreateWeekStaffScheduleExportArgs): Promise<void> => {
		loadId.current += 1;
		const thisLoadId = loadId.current;
		setIsLoading(true);
		try {
			const buffer = await createWeekStaffScheduleExport(args);
			// Wrap is safe to carry here because the sheet sizes its own entry rows — without it the
			// preview would clip a shift's location and job at the column edge.
			const parsed = await excelBufferToFortuneSheet(buffer, args.columnWidthOverrides, { applyWrapText: true });
			if (thisLoadId !== loadId.current) return;
			setSheets(parsed);
		} catch (error) {
			if (thisLoadId !== loadId.current) return;
			setSheets([]);
			onErrorRef.current(error);
		} finally {
			if (thisLoadId === loadId.current) setIsLoading(false);
		}
	}, []);

	useEffect(() => {
		if (!buildArgs) {
			setSheets([]);
			return;
		}
		// Debounced so holding a number field's stepper doesn't rebuild the whole workbook per tick.
		const timer = setTimeout(() => void load(buildArgs), PREVIEW_DEBOUNCE_MS);
		return () => clearTimeout(timer);
	}, [buildArgs, load]);

	return { sheets, isLoading };
};
